import type { ChallengeConfig, ChallengeType } from './types';

const CHALLENGE_TYPES: ChallengeType[] = [
  'head_turn_left',
  'head_turn_right',
  'pitch_up',
  'pitch_down',
  'blink',
  'mouth_open',
];

const FIRST_CHALLENGE_TYPES: ChallengeType[] = [
  'head_turn_left',
  'head_turn_right',
];

export class Scheduler {
  private timeout: ReturnType<typeof setTimeout> | null = null;

  private remainingMs: number | null = null;

  private pausedAt: number | null = null;

  private lastType: ChallengeType | null = null;

  private isFirstChallenge = true;

  private config: ChallengeConfig;

  private onScheduled: (type: ChallengeType) => void;

  constructor(
    config: ChallengeConfig,
    onScheduled: (type: ChallengeType) => void,
  ) {
    this.config = config;
    this.onScheduled = onScheduled;
  }

  start(): void {
    this.schedule();
  }

  pause(): void {
    if (this.timeout !== null) {
      clearTimeout(this.timeout);
      this.timeout = null;
      this.pausedAt = Date.now();
      this.remainingMs = this.remainingMs ?? 0;
    }
  }

  resume(): void {
    if (this.pausedAt !== null && this.remainingMs !== null) {
      const elapsed = Date.now() - this.pausedAt;
      const remaining = Math.max(this.remainingMs - elapsed, 0);
      this.pausedAt = null;
      this.remainingMs = null;

      if (remaining <= 0) {
        this.schedule();
      } else {
        this.timeout = setTimeout(() => {
          this.schedule();
        }, remaining);
      }
    }
  }

  stop(): void {
    if (this.timeout !== null) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    this.remainingMs = null;
    this.pausedAt = null;
  }

  private schedule(): void {
    const type = this.pickType();
    const interval = this.randomInterval();

    this.remainingMs = interval;

    this.timeout = setTimeout(() => {
      this.timeout = null;
      this.onScheduled(type);
      this.isFirstChallenge = false;
      this.schedule();
    }, interval);
  }

  private randomInterval(): number {
    if (this.isFirstChallenge) {
      const minMs = this.config.firstChallengeMinMinutes * 60 * 1000;
      const maxMs = this.config.firstChallengeMaxMinutes * 60 * 1000;
      return minMs + Math.random() * (maxMs - minMs);
    }

    const minMs = this.config.minIntervalMinutes * 60 * 1000;
    const maxMs = this.config.maxIntervalMinutes * 60 * 1000;
    return minMs + Math.random() * (maxMs - minMs);
  }

  private pickType(): ChallengeType {
    const pool = this.isFirstChallenge
      ? FIRST_CHALLENGE_TYPES
      : CHALLENGE_TYPES;

    const available = this.lastType
      ? pool.filter(t => t !== this.lastType)
      : pool;

    const type = available[Math.floor(Math.random() * available.length)];
    this.lastType = type;
    return type;
  }
}
